import React, { useEffect, useState } from 'react';
import './NotificationPrompt.css';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext.tsx';

declare global {
    interface Window {
        OneSignal: any;
        OneSignalDeferred: any[];
    }
}

const NotificationPrompt = () => {
    const [show, setShow] = useState(false);
    const { user, isAuthenticated } = useAuth();

    useEffect(() => {
        // إذا لم يكن المستخدم مسجل دخول، لا تعرض الإشعارات
        if (!isAuthenticated || !user) {
            setShow(false);
            //console.log('User is not authenticated or user is null');
            return;
        }

        const isDismissed = sessionStorage.getItem('notificationPromptDismissed');
        if (isDismissed === 'true') return;

        const checkPermission = async () => {
            try {
                // التحقق من دعم الإشعارات و Service Worker
                if (!('Notification' in window)) {
                    //console.log('Notifications not supported');
                    return;
                }

                if (!('serviceWorker' in navigator)) {
                    //console.log('Service Worker not supported');
                    return;
                }

                const permission = Notification.permission;
                //console.log('Current permission:', permission);
                // أظهر المطالبة فقط إذا كان المستخدم مسجل دخول وإذن الإشعارات "default"
                setShow(permission === 'default' && isAuthenticated);
            } catch (error) {
                console.error('Error checking notification permission:', error);
            }
        };

        checkPermission();
        window.addEventListener('focus', checkPermission);
        return () => window.removeEventListener('focus', checkPermission);
    }, [isAuthenticated, user]);

    const handleEnable = async () => {
        try {
            // التحقق من تسجيل الدخول
            if (!isAuthenticated || !user) {
                toast.error('يجب تسجيل الدخول أولاً لتفعيل الإشعارات', { autoClose: 500 });
                return;
            }

            // التحقق من دعم الإشعارات
            if (!('Notification' in window)) {
                toast.error('متصفحك لا يدعم الإشعارات', { autoClose: 500 });
                return;
            }

            // التحقق من دعم Service Worker
            if (!('serviceWorker' in navigator)) {
                toast.error('متصفحك لا يدعم خدمة الإشعارات', { autoClose: 500 });
                return;
            }

            // تسجيل Service Worker إذا لم يكن مسجلاً
            try {
                const registration = await navigator.serviceWorker.register('/service-worker.js');
                //console.log('Service Worker registered:', registration);
            } catch (error) {
                console.error('Service Worker registration failed:', error);
            }

            // طلب إذن الإشعارات من خلال OneSignal
            if (window.OneSignal) {
                try {
                    // ربط معرف المستخدم قبل طلب الإذن
                    if (user.id) {
                        await window.OneSignal.login(user.id.toString());
                    }
                    
                    // طلب إذن الإشعارات
                    const permission = await window.OneSignal.Notifications.requestPermission();
                    
                    if (permission) {
                        // تم منح الإذن
                        setShow(false);
                        toast.success('تم تفعيل الإشعارات بنجاح!', { autoClose: 500 });
                        
                        // تأكيد ربط معرف المستخدم بعد منح الإذن
                        if (user.id) {
                            await window.OneSignal.login(user.id.toString());
                        }

                        // إرسال إشعار ترحيبي
                        await window.OneSignal.Notifications.sendSelfNotification(
                            "مرحباً بك!",
                            "تم تفعيل الإشعارات بنجاح",
                            window.location.origin,
                            '/logo192.png',
                            {
                                dir: 'rtl',
                                tag: 'welcome',
                                requireInteraction: true
                            }
                        );

                        // حفظ حالة تفعيل الإشعارات في localStorage
                        localStorage.setItem('notificationsEnabled', 'true');
                        localStorage.setItem('notificationsEnabledTimestamp', Date.now().toString());
                    } else {
                        toast.error('لم يتم السماح بالإشعارات. يمكنك تفعيلها من إعدادات المتصفح', { autoClose: 500 });
                    }
                } catch (error) {
                    console.error('Error requesting OneSignal permission:', error);
                    toast.error('حدث خطأ أثناء تفعيل الإشعارات', { autoClose: 500 });
                }
            } else {
                console.error('OneSignal not initialized');
                toast.error('حدث خطأ في تهيئة نظام الإشعارات', { autoClose: 500 });
            }
        } catch (error) {
            console.error('Error enabling notifications:', error);
            toast.error('حدث خطأ أثناء تفعيل الإشعارات. تأكد من أن متصفحك يدعم الإشعارات وأنك تستخدم HTTPS', { autoClose: 500 });
        }
    };

    const handleDismiss = () => {
        sessionStorage.setItem('notificationPromptDismissed', 'true');
        setShow(false);
    };

    // إذا لم يكن المستخدم مسجل دخول أو الإظهار خاطئ، لا تعرض الـ UI
    if (!show || !isAuthenticated) return null;

    return (
        <div className="notification-prompt">
            <div className="notification-content">
                <div className="notification-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" fill="white"/>
                    </svg>
                </div>
                <div className="notification-text">
                    <h3>فعّل الإشعارات!</h3>
                    <p>لا تفوت أي تحديثات جديدة</p>
                </div>
            </div>
            <div className="notification-buttons">
                <button onClick={handleEnable} className="enable-button">
                    <span>تفعيل الإشعارات</span>
                    <svg viewBox="0 0 24 24" className="arrow-icon">
                        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                    </svg>
                </button>
                <button onClick={handleDismiss} className="cancel-button">
                    لاحقاً
                </button>
            </div>
        </div>
    );
};

export default NotificationPrompt; 